import isTest from '@purecarslabs/signal-utils/env/isTest'
/* if-not-on-site-start */
import authRedirectMiddleware from './auth-redirect'
import errorRedirectMiddleware from './error-redirect'
/* if-not-on-site-end */
import logAPIThrowsMiddleware from '@purecarslabs/signal-core/baseline/data/middleware/log-api-throws'
import toastrMiddleware from './toastr-middleware'
import throwAPIErrorsMiddleware from './throw-api-errors'
import {
  DataMiddlewareOptionsType,
  DataMiddlewareType,
  ErrorWithResponseType,
  ResponseOrErrorType
} from './types'

const STANDARD_MIDDLEWARE = [
  /* if-not-on-site-start */
  authRedirectMiddleware(),
  /* if-not-on-site-end */
  throwAPIErrorsMiddleware(/[4-5]\d\d/),
  logAPIThrowsMiddleware()
] as DataMiddlewareType[]

// no auth redirects in unit test so we can test other redirects.  auth redirect will be tested directly
const STANDARD_MIDDLEWARE_JEST = [
  throwAPIErrorsMiddleware(/[4-5]\d\d/)
] as DataMiddlewareType[]

type LastFetchDetailsType = {
  args: unknown[]
  middlewareOptions: DataMiddlewareOptionsType
}

let lastFetchDetails: LastFetchDetailsType
export const clearLastFetchDetails = (): void => {
  lastFetchDetails = {} as LastFetchDetailsType
} // for unit testing
export const getLastFetchDetails = (): LastFetchDetailsType => lastFetchDetails // for unit testing

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FetcherWrapperType = (...args: any[]) => Promise<Response>

const applyMiddleware = (
  middleware: DataMiddlewareType[] = isTest()
    ? STANDARD_MIDDLEWARE_JEST
    : STANDARD_MIDDLEWARE
) => (fetch: FetcherWrapperType) => (
  middlewareOptions: DataMiddlewareOptionsType = {}
) => (...fetchArgs: unknown[]): Promise<Response> => {
  const {
    showToastr,
    fetchContext,
    /* if-not-on-site-start */
    redirectOnError,
    mockData,
    mockDelay,
    mockError,
    /* if-not-on-site-end */
    showSuccessToast,
    successVerb,
    useMockData
  } = middlewareOptions

  lastFetchDetails = {
    args: fetchArgs,
    middlewareOptions
  }

  /* if-not-on-site-start */
  if (mockData && useMockData) {
    if (showToastr) {
      toastrMiddleware({
        showToastr,
        fetchContext,
        showSuccessToast,
        successVerb
      })()
    }

    return Promise.resolve({
      status: 200,
      json: (): Promise<
        Record<string, unknown> | Array<Record<string, unknown>>
      > =>
        new Promise(resolve => {
          setTimeout(() => {
            resolve(mockData)
          }, mockDelay || 1000)
        })
    } as unknown) as Promise<Response>
  }
  /* if-not-on-site-end */

  const middlewareUse = middleware.map(middlewareFunc => middlewareFunc)

  /* if-not-on-site-start */
  if (redirectOnError) {
    middlewareUse.push(errorRedirectMiddleware(redirectOnError, mockError))
  }
  /* if-not-on-site-end */

  if (showToastr) {
    middlewareUse.push(
      toastrMiddleware({
        showToastr,
        fetchContext,
        showSuccessToast,
        successVerb
      })
    )
  }

  const handleError = (err: ErrorWithResponseType): void => {
    // Enrich network-level errors (e.g. TypeError: Failed to fetch) with the
    // URL that was being fetched so downstream middleware/loggers can report it.
    if (!err.url && fetchArgs[0]) {
      err.url = String(fetchArgs[0])
    }
    middlewareUse.forEach(middlewareFunc => middlewareFunc(err))
    throw err
  }

  /* if-not-on-site-start */
  if (mockError) {
    handleError(mockError)
    return Promise.reject(mockError)
  }
  /* if-not-on-site-end */

  const callNextMiddleware = (
    middlewareOn: number,
    resp: ResponseOrErrorType
  ): Promise<ResponseOrErrorType> =>
    resp && middlewareOn < middlewareUse.length
      ? middlewareUse[middlewareOn](resp).then(
          (_resp: ResponseOrErrorType): Promise<ResponseOrErrorType> =>
            callNextMiddleware(middlewareOn + 1, _resp)
        )
      : Promise.resolve(resp)

  return fetch(...fetchArgs)
    .then(
      (resp: Response): Promise<ResponseOrErrorType> =>
        callNextMiddleware(0, resp)
    )
    .catch((err: ErrorWithResponseType): void => {
      handleError(err)
    }) as Promise<Response>
}

export default applyMiddleware
